import dayjs from 'dayjs';
import { SidekickChatSession } from 'src/graphql';
import { htmlToPlainText } from 'src/utils/text-parsing';
import { AssistantBubbleState } from './assistant-content';

export interface SidekickRunExtraData {
  chunk?: string;
  status?: string;
  errorMessage?: string;
}

export function extractSidekickExtra(extraData: unknown): SidekickRunExtraData | undefined {
  if (!extraData || typeof extraData !== 'object') return undefined;
  const entityChange = (extraData as { entityChange?: Record<string, SidekickRunExtraData> })
    .entityChange;
  return entityChange?.SidekickRun;
}

export function normalizeSuggestedQuestions(raw: string[]): string[] {
  const seen = new Set<string>();

  const add = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
  };

  const splitNumbered = (value: string) => {
    const parts = value.split(/\s*\d+\)\s+/).map((part) => part.trim());
    const filtered = parts.filter(Boolean);
    if (filtered.length <= 1) {
      add(value);
      return;
    }
    filtered.forEach(add);
  };

  raw.forEach((item) => {
    let payload = item;
    try {
      const parsed = JSON.parse(item) as {
        assistantMessage?: { text?: string };
        suggestedQuestions?: string[];
      };
      if (parsed?.assistantMessage?.text) {
        payload = parsed.assistantMessage.text;
      } else if (Array.isArray(parsed?.suggestedQuestions)) {
        parsed.suggestedQuestions.forEach(add);
        return;
      }
    } catch {
      // not JSON, continue
    }

    splitNumbered(payload);
  });

  return Array.from(seen);
}

export function mergeSessions(
  baseSessions: SidekickChatSession[],
  previousSessions: SidekickChatSession[],
): SidekickChatSession[] {
  const merged = new Map<string, SidekickChatSession>();
  baseSessions.forEach((session) => merged.set(session.sessionRootRunId, session));
  previousSessions.forEach((session) => {
    const existing = merged.get(session.sessionRootRunId);
    if (!existing) {
      merged.set(session.sessionRootRunId, session);
      return;
    }
    if (!existing.previewText && session.previewText) {
      merged.set(session.sessionRootRunId, {
        ...existing,
        previewText: session.previewText,
      });
    }
  });
  const getTimestamp = (session: SidekickChatSession) =>
    dayjs(session.updatedAt || session.createdAt).valueOf();
  return Array.from(merged.values()).sort((a, b) => getTimestamp(b) - getTimestamp(a));
}

type AssistantCopyContent = { plainText: string; html?: string };

export function getAssistantCopyContent(
  bubble?: AssistantBubbleState,
): AssistantCopyContent | null {
  if (!bubble) return null;

  const normalizePlain = (value: string) => htmlToPlainText(value) || value;
  const looksLikeHtml = (value: string) => /<[a-z][\s\S]*>/i.test(value);
  const ensureContent = (content: AssistantCopyContent | null) =>
    content && (content.plainText.trim() || content.html) ? content : null;

  const extractFromJson = (payload: string) => {
    try {
      const parsed = JSON.parse(payload) as {
        assistantMessage?: { text?: string | null; html?: string | null };
      };
      const text = parsed.assistantMessage?.text || undefined;
      const html = parsed.assistantMessage?.html || undefined;
      if (text || html) {
        const plainText = normalizePlain(html || text || '');
        return ensureContent({ plainText, html });
      }
    } catch {
      // not JSON
    }
    return null;
  };

  if (bubble.html) {
    const plainText = normalizePlain(bubble.html);
    return ensureContent({ plainText, html: bubble.html });
  }

  if (bubble.text) {
    const jsonContent = extractFromJson(bubble.text);
    if (jsonContent) return jsonContent;

    const plainText = normalizePlain(bubble.text);
    const html = looksLikeHtml(bubble.text) ? bubble.text : undefined;
    return ensureContent({ plainText, html });
  }

  return null;
}
